import { Lock, UserMinus, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { avatarColour, initialsOf, type WorkspaceMember } from './types'

export function Avatar({
  member,
  size = 'md',
}: {
  member: { id: string; name: string | null; email: string }
  size?: 'sm' | 'md'
}) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-semibold text-white',
        size === 'sm' ? 'size-6 text-[10px]' : 'size-8 text-[11px]',
      )}
      style={{ backgroundColor: avatarColour(member.id) }}
      title={member.name ?? member.email}
    >
      {initialsOf(member)}
    </span>
  )
}

/** The overlapping avatar cluster on the overview cards. */
export function AvatarStack({
  members,
  total,
}: {
  members: { id: string; name: string | null; email: string }[]
  total: number
}) {
  return (
    <span className="flex items-center gap-2">
      <span className="flex -space-x-1.5">
        {members.map((member) => (
          <span key={member.id} className="rounded-full ring-2 ring-paper">
            <Avatar member={member} size="sm" />
          </span>
        ))}
      </span>
      <span className="text-[12px] text-ink-muted">
        {total} {total === 1 ? 'person' : 'people'}
      </span>
    </span>
  )
}

export function MemberRows({
  members,
  ownerId,
  canManage,
  currentUserId,
  onRemove,
  removingId,
}: {
  members: WorkspaceMember[]
  ownerId: string
  canManage: boolean
  currentUserId: string | undefined
  onRemove: (member: WorkspaceMember) => void
  removingId: string | null
}) {
  return (
    <ul className="divide-y divide-line">
      {members.map((member) => {
        const isOwner = member.id === ownerId
        const isSelf = member.id === currentUserId

        return (
          <li key={member.id} className="flex items-center gap-3 py-2.5">
            <Avatar member={member} />

            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-[13px] font-semibold text-ink">
                <span className="truncate">{member.name || member.email}</span>
                {isOwner && (
                  <span className="rounded-full bg-brand-soft px-1.5 py-0.5 text-[10px] font-semibold text-brand">
                    Owner
                  </span>
                )}
              </p>
              <p className="truncate text-[12px] text-ink-muted">{member.email}</p>
            </div>

            {isOwner ? (
              <span className="flex shrink-0 items-center gap-1 text-[11px] text-ink-faint">
                <Lock className="size-3" />
                Project owner
              </span>
            ) : (
              (canManage || isSelf) && (
                <Button
                  variant="outlineBrand"
                  size="sm"
                  disabled={removingId === member.id}
                  onClick={() => onRemove(member)}
                >
                  <UserMinus className="size-3.5" />
                  {isSelf && !canManage ? 'Leave' : 'Remove'}
                </Button>
              )
            )}
          </li>
        )
      })}
    </ul>
  )
}

export function InviteButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" onClick={onClick}>
      <UserPlus className="size-3.5" />
      Invite
    </Button>
  )
}
